import { jsPDF } from 'jspdf';
import '../fonts/inter'; // registers the "Inter" font family with jsPDF

// Helpers
const sanitizePart = (s) => {
    if (!s) return '';
    // Strip accents/diacritics, keep only letters/numbers/underscore, collapse underscores
    return s
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // remove diacritics
        .replace(/[^A-Za-z0-9]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
        .toLowerCase();
};

// courseComplexity is a human-readable string like "Basic", "Intermediate", "Specialist"
async function generateCertificate(courseTitle, courseComplexity, userName, userSurname, courseFile, authenticityCode) {
    console.log('[CertGen] Starting certificate generation...');
    const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'px',
        format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Helper function to load images asynchronously
    const loadImage = (src) => {
        console.log(`[CertGen] Loading image: ${src}`);
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                console.log(`[CertGen] Successfully loaded image: ${src}`);
                resolve(img);
            };
            img.onerror = (err) => {
                console.error(`[CertGen] Failed to load image: ${src}`, err);
                reject(new Error(`Failed to load image: ${src}`));
            };
            img.src = src;
        });
    };

    try {
        console.log('[CertGen] Loading certificate template...');
        const templateImg = await loadImage('/templates/Certificate_Template.jpg');
        console.log('[CertGen] Template loaded successfully.');

        // --- Add Template Background ---
        // Use the template image as full background
        doc.addImage(templateImg, 'JPEG', 0, 0, pageWidth, pageHeight);

        // --- Certificate Content Overlay ---

        // Main Title - moved up to center better in white space
        doc.setFont('Inter', 'bold');
        doc.setFontSize(28);
        doc.setTextColor(50, 50, 50);
        doc.text("Training Academy", pageWidth / 2, 100, { align: 'center' });
        doc.text("Certificate of Completion", pageWidth / 2, 130, { align: 'center' });

        // Sub-heading
        doc.setFont('Inter', 'normal');
        doc.setFontSize(14);
        doc.setTextColor(80, 80, 80);
        doc.text("is proudly presented to", pageWidth / 2, 160, { align: 'center' });

        // User's Name - prominent and elegant
        doc.setFont('times', 'bolditalic');
        doc.setFontSize(42);
        doc.setTextColor(0, 84, 166); // DeepAlert blue
        doc.text(`${userName} ${userSurname}`, pageWidth / 2, 200, { align: 'center' });

        // Course completion text with complexity
        doc.setFont('Inter', 'normal');
        doc.setFontSize(14);
        doc.setTextColor(50, 50, 50);
        const complexityText = (courseComplexity ? `${courseComplexity} ` : '');
        doc.text(`For successfully completing the ${complexityText}course:`, pageWidth / 2, 230, { align: 'center' });

        // Course Title
        doc.setFont('Inter', 'bold');
        doc.setFontSize(18);
        doc.setTextColor(0, 84, 166);
        const courseTitleText = doc.splitTextToSize(courseTitle, pageWidth - 200);
        doc.text(courseTitleText, pageWidth / 2, 260, { align: 'center' });

        // Date and authenticity code - positioned closer to course title
        const today = new Date();
        const dateStr = `${today.toLocaleString('default', { month: 'long' })} ${today.getDate()}, ${today.getFullYear()}`;

        doc.setFontSize(10);
        doc.setFont('Inter', 'normal');
        doc.setTextColor(80, 80, 80);
        doc.text(dateStr, pageWidth / 2, 280, { align: 'center' });

        doc.setFontSize(8);
        doc.setFont('Inter', 'italic');
        const verifyText = "Verify authenticity at academy.deepalert.ai/verify";
        doc.text(verifyText, pageWidth / 2, 295, { align: 'center' });

        const codeText = `authenticity code: ${authenticityCode}`;
        doc.text(codeText, pageWidth / 2, 308, { align: 'center' });

        // Save the PDF with sanitized, timestamped filename
        const now = new Date();
        const yyyy = String(now.getFullYear());
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const HH = String(now.getHours()).padStart(2, '0');
        const MM = String(now.getMinutes()).padStart(2, '0');
        const dateStamp = `${yyyy}${mm}${dd}_${HH}${MM}`;
        const fileName = `${sanitizePart(userName)}_${sanitizePart(userSurname)}_${sanitizePart(authenticityCode)}_${dateStamp}.pdf`;
        console.log(`[CertGen] Attempting to save PDF as: ${fileName}`);
        doc.save(fileName);
        return { success: true, fileName };

    } catch (error) {
        console.error("Error generating certificate:", error);
        return { success: false, error: error.message };
    }
}

export default generateCertificate;
